// @givmo/app-charity — profile editor (CPA-WEB6): the live `/profile` route while the tabbed
// Profile page (PE-WEB1) is compiled dark.
//
// Where a charity edits its public profile — mission / website / contact + logo — and sees its READ-ONLY
// verification domain. The BODY — the active-org resolution, the Owner gate (FAIL CLOSED), the
// profile read, the form, the logo uploader and the domain guard — lives in `ProfileFactsPanel`,
// which the tabbed page's Details tab renders too: ONE editor, two frames (the transplant is a MOVE,
// D-PE-5). This file keeps only what is this route's own: the page frame and the `page-profile` root
// (the data contract e2e/guard assert). Every gate, every testid and every state renders exactly as
// the editor drew them before its body moved, and the pixels are unchanged; the DOM is the tree the
// editor drew plus ONE wrapper element — the panel's root, `profile-facts` (an Owner) /
// `profile-facts-readonly` (a Member) — the Profile Enrichment live-route invariant, pinned by
// `profile-page.test.tsx` and `router/profile-route.test.tsx`.

import { PageHeader, testIdProp } from '@givmo/design-system';
import { useActiveOrg } from '../../org/active-org-context';
import { ProfileFactsPanel } from './profile-facts-panel';

const PREFIX = 'profile';

export function ProfilePage() {
  const { activeMembership: membership, activeCharityId: charityId } = useActiveOrg();
  // The header chip carries the organization's name once BOTH the membership and its id resolved —
  // exactly the frames the editor drew before its body moved (none while pending, errored or
  // without a verified organization).
  const orgName = membership !== null && charityId != null ? membership.name : undefined;
  return (
    <PageShell orgName={orgName}>
      <ProfileFactsPanel />
    </PageShell>
  );
}

// ── Page frame ──────────────────────────────────────────────────────────────────────────────────────
function PageShell({ children, orgName }: { children: React.ReactNode; orgName?: string }) {
  return (
    <div
      className="mx-auto flex w-full max-w-[720px] flex-col gap-16 px-24 py-24"
      {...testIdProp('page-profile')}
    >
      <PageHeader
        title="Profile"
        subtitle="Edit your organization’s public profile — mission, website, contact, and logo."
        organizationName={orgName}
        organizationNameTestId={`${PREFIX}-org-badge`}
      />
      {children}
    </div>
  );
}
